import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  deleteUnit,
  setWalkThroughStatus,
  onClearResponseDone,
} from 'src/store/actions/suites';
import { updateSuiteSynchronization } from 'src/services/client';
import SuitesTable from '../../components/SuitesTable';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import { clearSearch } from 'src/store/actions/search';
import { selectPRLoader } from 'src/store/selectors/PropertyLoader';
import { fetchPropertyCounts } from 'src/store/actions/properties';
import { clearFaqsNotesDocuments } from 'src/store/actions/faqsNotesDocuments';
import { getAllUserTypeRoles } from 'src/store/actions/formOptions';
import { clearValidation } from 'src/store/actions/validation';
import { toast } from 'react-toastify';

interface PropertySuitesProps {
  property: any;
  suites: any[];
  isLoadingSuites: boolean;
  refetchSuites: () => void;
}

const PropertySuites = ({
  property,
  suites,
  isLoadingSuites,
  refetchSuites,
}: PropertySuitesProps) => {
  const dispatch = useDispatch();
  const user = useSelector(selectAuthenticatedUser);

  // TODO: phase N — selectPRLoader + the mutation dispatches below stay on Redux;
  // only the suites fetch was lifted into usePropertyPageOrchestrator.
  const loader = useSelector(selectPRLoader);

  const onDeleteUnit = (unitId: any, userId: any) =>
    dispatch(deleteUnit(unitId, userId));
  const onSetWalkThroughStatus = (status: any, data: any) =>
    dispatch(setWalkThroughStatus(status, data));

  const onClearResponse = () => dispatch(onClearResponseDone());
  const onClearValidation = () => dispatch(clearValidation());
  const onFetchPropertyCounts = (propertyId: any, userId: any, roleName: any) =>
    dispatch(fetchPropertyCounts(propertyId, userId, roleName));
  const onClearFaqsNotesDocuments = () => dispatch(clearFaqsNotesDocuments());
  const onGetAllUserTypeRoles = () => dispatch(getAllUserTypeRoles());
  const onClearSearch = () => dispatch(clearSearch());

  useEffect(() => {
    onClearFaqsNotesDocuments();
    onClearSearch();
    onGetAllUserTypeRoles();
    clearSuitesImportResponse();
  }, []);

  const clearSuitesImportResponse = () => {
    onClearValidation();
    onClearResponse();
  };

  const handleDeleteUnit = async (unitId: any) => {
    onDeleteUnit(unitId, user.id);
    onFetchPropertyCounts(
      property.propertyId,
      user.id,
      user.roles[0].role.name
    );
  };

  const handleSetWalkThroughStatus = (status: any, data: any) => {
    onSetWalkThroughStatus(status, data);
  };

  const handleToggleMobileSync = (
    suiteId: string,
    mobileSyncAllowed: boolean
  ) => {
    updateSuiteSynchronization(suiteId, mobileSyncAllowed)
      .then((res) => {
        if (res instanceof Response && !res.ok) {
          toast.error('🚫 Mobile Sync update failed.', {
            autoClose: 10000,
            position: 'top-center',
          });
          return;
        }

        refetchSuites();
      })
      .catch((err) => {
        toast.error('🚫 Mobile Sync update failed.', {
          autoClose: 10000,
          position: 'top-center',
        });
      });
  };

  const isLoading: any = loader || isLoadingSuites;

  return (
    <SuitesTable
      suites={suites}
      property={property}
      onDeleteUnit={(data: any) => handleDeleteUnit(data)}
      setWalkThroughStatus={(status: any, data: any) =>
        handleSetWalkThroughStatus(status, data)
      }
      onToggleMobileSync={(suiteId, mobileSyncAllowed) =>
        handleToggleMobileSync(suiteId, mobileSyncAllowed)
      }
      fetchPropertySuites={() => refetchSuites()}
      loading={isLoading}
    />
  );
};

export default PropertySuites;
